import { useEffect, useMemo, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api, useQuery } from './api.js';
import { useRoleBar, useSession, useTheme } from './session.js';
import { LiveProvider, useLive } from './live.js';
import { Spinner } from './ui.js';
import { Menu, MenuItem, MenuSeparator, Segmented } from './controls.js';
import { Avatar, AvatarPicker } from './avatar.js';
import {
  BookIcon, CalendarIcon, ChevronDown, ChevronRight, DocIcon, GaugeIcon, GraduationIcon,
  GridIcon, GearIcon, ListIcon, LockIcon, MailIcon, MoonIcon, MoreIcon, PeopleIcon, PhoneIcon,
  PlaneIcon, SunIcon, SignOutIcon, WalletIcon,
} from './icons.js';
import type { OrgType } from '../../shared/types.js';

import { Login } from './screens/Login.js';
import { ResetPassword } from './screens/ResetPassword.js';
import { ChangePassword } from './screens/ChangePassword.js';
import { Signup } from './screens/Signup.js';
import { Landing } from './screens/Landing.js';
import { About } from './screens/About.js';
import { Contact } from './screens/Contact.js';
import { Privacy } from './screens/Privacy.js';
import { Security } from './screens/Security.js';
import { Status } from './screens/Status.js';
import { Support } from './screens/Support.js';
import { Terms } from './screens/Terms.js';
import { Schedule } from './screens/Schedule.js';
import { Fleet } from './screens/Fleet.js';
import { Instructors } from './screens/Instructors.js';
import { Students } from './screens/Students.js';
import { StudentRecord } from './screens/StudentRecord.js';
import { Portal } from './screens/Portal.js';
import { Billing } from './screens/Billing.js';
import { Mobile } from './screens/Mobile.js';
import { Access } from './screens/Access.js';
import { Logbook } from './screens/Logbook.js';
import { Records } from './screens/Records.js';
import { Inbox } from './screens/Inbox.js';
import { PortalDocuments } from './screens/PortalDocuments.js';
import { Drawer } from './ui.js';
import type { QueuedRequest } from './screens/RequestQueue.js';

const PATH: Record<string, string> = {
  schedule: '/schedule', fleet: '/fleet', instructors: '/instructors',
  students: '/students', portal: '/portal', billing: '/billing',
  mobile: '/mobile', access: '/access', logbook: '/logbook',
  records: '/records',
};

/** Glyph per view, used by the phone tab bar. */
const GLYPH: Record<string, (p: { className?: string }) => JSX.Element> = {
  schedule: CalendarIcon, fleet: PlaneIcon, instructors: PeopleIcon,
  students: GraduationIcon, portal: GraduationIcon, billing: WalletIcon,
  mobile: PhoneIcon, access: GearIcon, logbook: BookIcon, records: ListIcon,
};

/** How many tabs a phone bar holds before the rest go behind More. */
const TAB_SLOTS = 5;

/** Short labels so five tabs fit across a phone. */
const SHORT: Record<string, string> = {
  schedule: 'Schedule', fleet: 'Aircraft', instructors: 'CFIs',
  students: 'Students', portal: 'Portal', billing: 'Billing',
  mobile: 'Ramp', access: 'Setup', logbook: 'Logbook', records: 'Records',
};

/**
 * Pages that exist independently of a session. They are registered in both route
 * tables below: a signed-in user following a link to the terms should read the
 * terms, not be bounced to their schedule.
 */
const PUBLIC_ROUTES: [string, () => JSX.Element][] = [
  ['/about', About],
  ['/contact', Contact],
  ['/support', Support],
  ['/status', Status],
  ['/privacy', Privacy],
  ['/terms', Terms],
  ['/security', Security],
];

function publicRoutes(): JSX.Element[] {
  return PUBLIC_ROUTES.map(([path, Screen]) => (
    <Route key={path} path={path} element={<Screen />} />
  ));
}

export function App(): JSX.Element {
  const { session, loading } = useSession();
  const location = useLocation();

  if (loading) return <Spinner label="Opening FlightSlots…" />;

  if (!session) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/reset" element={<ResetPassword />} />
        <Route path="/" element={<Landing />} />
        {publicRoutes()}
        <Route path="*" element={<Navigate to="/login" replace state={{ from: location.pathname }} />} />
      </Routes>
    );
  }

  return (
    <LiveProvider enabled>
      <Shell />
    </LiveProvider>
  );
}

/**
 * Look at the school from another desk. The session really does become that
 * account's, so what is on screen is what that role would actually see — a
 * mock-up of permissions is worth nothing.
 */
function DemoSwitch({ onChanged }: { onChanged: () => Promise<void> | void }): JSX.Element | null {
  const { data } = useQuery<{ name: string; email: string; role: string; initials: string; note: string }[]>('/demo-accounts');
  const [busy, setBusy] = useState(false);
  if (!data?.length) return null;

  const viewAs = async (email: string) => {
    setBusy(true);
    try {
      await api.post('/demo/view-as', { email });
      await onChanged();
    } finally { setBusy(false); }
  };

  return (
    <>
      <MenuSeparator />
      <div className="menu__label">View the school as</div>
      {data.map((a) => (
        <MenuItem key={a.email} icon={<PeopleIcon />} onClick={() => !busy && viewAs(a.email)}>
          {a.name} · {a.role}
        </MenuItem>
      ))}
    </>
  );
}

function Shell(): JSX.Element {
  const { session, hasView, logout, setOrgType, refresh } = useSession();
  const [theme, toggleTheme] = useTheme();
  const [roleBar, toggleRoleBar] = useRoleBar();
  const navigate = useNavigate();
  const location = useLocation();

  const notifications = useQuery<{ id: number; title: string; body: string; read: boolean; href: string }[]>('/notifications');
  const requests = useQuery<{ requests: QueuedRequest[] }>(
    hasView('schedule') ? '/schedule/requests' : null,
  );
  const chats = useQuery<{ unread: number }>('/inbox/threads');
  const unread = notifications.data?.filter((n) => !n.read).length ?? 0;
  const waiting = requests.data?.requests.length ?? 0;
  const unreadChats = chats.data?.unread ?? 0;
  // The bell counts anything asking for attention.
  const pending = unread + waiting + unreadChats;

  const [inboxOpen, setInboxOpen] = useState(false);
  // `?dm=<user>` is how another screen asks for a conversation: a student's
  // record links here rather than growing a chat panel of its own.
  const dm = Number(new URLSearchParams(location.search).get('dm')) || null;
  useEffect(() => { if (dm) setInboxOpen(true); }, [dm]);
  const closeInbox = () => {
    setInboxOpen(false);
    if (dm) {
      const next = new URLSearchParams(location.search);
      next.delete('dm');
      navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
    }
  };
  const [docsOpen, setDocsOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const refreshBadges = () => { notifications.reload(); requests.reload(); };
  // A lesson requested, confirmed or moved changes both of these, and neither
  // should wait for the person to reload the page to find out.
  useLive('notifications', notifications.reload);
  useLive('messages', chats.reload);
  useLive('schedule', refreshBadges);

  const home = PATH[session!.views[0]?.id ?? 'schedule'];

  /*
   * The ramp view is a phone held next to an aircraft: today's flights, big
   * enough to tap with the engine running. On a phone everybody starts there,
   * because that is the screen they walked to the aeroplane for.
   *
   * On a desk, staff already have the board, so the ramp would be a second
   * drawing of the same day and is dropped from the strip. A student has no
   * board — and checking in lives only here — so for them it stays.
   */
  const deskViews = useMemo(
    () => (session!.user?.role === 'student'
      ? session!.views
      : session!.views.filter((v) => v.id !== 'mobile')),
    [session],
  );
  const phoneViews = useMemo(() => {
    const ramp = session!.views.filter((v) => v.id === 'mobile');
    return [...ramp, ...session!.views.filter((v) => v.id !== 'mobile')];
  }, [session]);

  /*
   * Five slots, and no more. A bottom bar that scrolls sideways hides its own
   * contents: a manager has eight sections, and the eighth would sit off the
   * right-hand edge with nothing to say it was there. So four sections keep
   * their place and the rest go behind one More tab, which is the only thing
   * that ever moves as the roster of sections changes.
   */
  const [moreOpen, setMoreOpen] = useState(false);
  const [barViews, moreViews] = useMemo(
    () => (phoneViews.length <= TAB_SLOTS
      ? [phoneViews, []]
      : [phoneViews.slice(0, TAB_SLOTS - 1), phoneViews.slice(TAB_SLOTS - 1)]),
    [phoneViews],
  );
  // The More tab lights up when the section you are on lives inside it.
  const inMore = moreViews.some((v) => location.pathname.startsWith(PATH[v.id]));
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  // A role or org change can strip the screen you are on; fall back gracefully.
  useEffect(() => {
    const current = Object.entries(PATH).find(([, p]) => location.pathname.startsWith(p));
    if (current && !hasView(current[0])) navigate(home, { replace: true });
  }, [location.pathname, hasView, home, navigate]);

  return (
    <div className="app">
      {session!.demo?.viewingAs && (
        <div className="viewing-as">
          <span>
            Viewing the school as <strong>{session!.demo.viewingAs}</strong>
            {session!.demo.realName && <> · signed in as {session!.demo.realName}</>}
          </span>
          <button className="btn btn--sm" onClick={async () => {
            await api.post('/demo/return');
            await refresh();
            navigate(home);
          }}>Back to my account</button>
        </div>
      )}
      <header className="topbar">
        <div className="row" style={{ gap: 20, minWidth: 0 }}>
          {/* Signed in, the mark goes home — the schedule, or the portal for a student. */}
          <NavLink
            to={home}
            className="topbar__brand"
            aria-label="FlightSlots home"
            onClick={() => window.scrollTo({ top: 0 })}
          >
            <span className="topbar__mark" />
            FlightSlots
          </NavLink>
          <div className="topbar__org">{session!.org.name} · {session!.org.icao}</div>
        </div>

        <div className="topbar__right">
          <span className="topbar__divider" />

          {/* One button, one meaning, and the same panel for everyone: an
              envelope opens your inbox over whatever you were looking at. What
              is inside it differs by role — a student sees where their requests
              got to, staff decide them — but the way in does not. */}
          <button
            className={pending ? 'chip chip--on' : 'chip chip--icon'}
            aria-label={`Inbox${pending ? `, ${pending} waiting` : ''}`}
            onClick={() => setInboxOpen(true)}
          >
            <MailIcon />
            {pending > 0 && <span>{pending}</span>}
          </button>

          <button className="chip chip--icon" onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light appearance' : 'Switch to dark appearance'}>
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </button>

          <Menu
            align="right"
            trigger={(p) => (
              <button {...p} className="chip" style={{ paddingLeft: 4 }} aria-label="Account">
                <Avatar
                  userId={session!.user!.id}
                  updatedAt={session!.user!.avatarUpdatedAt}
                  initials={session!.user!.initials}
                  size={24}
                />
                <span className="only-wide">{session!.user!.name}</span>
              </button>
            )}
          >
            <div className="row" style={{ padding: '10px', gap: 12, flexWrap: 'nowrap' }}
              onClick={(e) => e.stopPropagation()}>
              <AvatarPicker
                userId={session!.user!.id}
                updatedAt={session!.user!.avatarUpdatedAt}
                initials={session!.user!.initials}
                label={session!.user!.name}
                size={48}
                canEdit
                onChanged={refresh}
              />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 600 }}>{session!.user!.name}</span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--label-3)' }}>
                  {session!.roleLabel} · {session!.orgLabel}
                </span>
              </span>
            </div>
            <MenuSeparator />
            <MenuItem icon={<DocIcon />} onClick={() => setDocsOpen(true)}>
              School documents
            </MenuItem>
            <MenuItem icon={<LockIcon />} onClick={() => setPasswordOpen(true)}>
              Change password
            </MenuItem>
            {session!.demo?.canViewAs && <DemoSwitch onChanged={refresh} />}
            <MenuSeparator />
            <MenuItem icon={<SignOutIcon />} danger onClick={() => logout().then(() => navigate('/'))}>
              Sign out
            </MenuItem>
          </Menu>
        </div>
      </header>

      {/* With one section there is nothing to navigate between, so the rail and
          the phone tab bar are both suppressed. */}
      {deskViews.length > 1 && (
      <nav className="tabs" aria-label="Sections">
        {deskViews.map((v) => (
          <NavLink key={v.id} to={PATH[v.id]}
            className={({ isActive }) => (isActive ? 'tabs__tab tabs__tab--on' : 'tabs__tab')}>
            {v.label}
          </NavLink>
        ))}
      </nav>
      )}

      {roleBar && (
      <div className="rolebar">
        <span className="small" style={{ color: 'var(--label-3)' }}>Signed in as</span>
        <strong className="small">{session!.roleLabel}</strong>
        {session!.user!.role === 'management' ? (
          <select className="select select--inline" value={session!.org.type}
            onChange={(e) => setOrgType(e.target.value as OrgType)} aria-label="Organization type">
            <option value="freelance">Freelance CFI</option>
            <option value="part61">Part 61 school</option>
            <option value="part141">Part 141 school</option>
          </select>
        ) : (
          <span className="pill pill--gray">{session!.orgLabel}</span>
        )}
        <span className="rolebar__note">{session!.roleNote}</span>
        <button className="btn btn--sm" onClick={toggleRoleBar}
          title="Hide this bar. It is a demo affordance and would not ship.">
          Hide
        </button>
      </div>
      )}

      {/* The room the floating tab bar needs is only owed when there is one:
          a student has a single section, no bar, and no reason for 78px of
          empty page under their last card. */}
      <main style={{ flex: 1 }} className={phoneViews.length > 1 ? 'has-tabbar' : undefined}>
        <Routes>
          <Route path="/" element={<Navigate to={home} replace />} />
          <Route path="/login" element={<Navigate to={home} replace />} />
          <Route path="/signup" element={<Navigate to={home} replace />} />
          {/* A reset link is followed by whoever has the mailbox open, which is
              often a browser already signed in as somebody. Honour it. */}
          <Route path="/reset" element={<ResetPassword />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/fleet" element={<Fleet />} />
          <Route path="/instructors" element={<Instructors />} />
          <Route path="/students" element={<Students />} />
          <Route path="/students/:id" element={<StudentRecord />} />
          <Route path="/portal" element={<Portal />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/mobile" element={<Mobile />} />
          <Route path="/access" element={<Access />} />
          <Route path="/logbook" element={<LogbookRoute />} />
          <Route path="/records" element={<Records />} />
          {publicRoutes()}
          <Route path="*" element={<Navigate to={home} replace />} />
        </Routes>
      </main>

      {/* With the bar hidden the app looks as a customer would see it, so the
          way back is deliberately quiet. */}
      {!roleBar && (
        <button className="rolebar__restore" onClick={toggleRoleBar} title="Show the demo bar">
          <ChevronDown style={{ width: 14, height: 14 }} />
        </button>
      )}

      {docsOpen && (
        <Drawer
          title="School documents"
          subtitle="Manuals, policies, checklists and forms"
          onClose={() => setDocsOpen(false)}
        >
          <div style={{ padding: '18px 24px 24px' }}>
            <PortalDocuments />
          </div>
        </Drawer>
      )}

      {passwordOpen && <ChangePassword onClose={() => setPasswordOpen(false)} />}

      {inboxOpen && (
        <Inbox
          openWith={dm}
          onClose={closeInbox}
          onAssign={(request) => navigate(`/schedule?assign=${request.id}`)}
          onChanged={refreshBadges}
        />
      )}

      {/* Phone navigation. Hidden above 720px, where the tab strip takes over. */}
      {phoneViews.length > 1 && (
      <nav className="tabbar glass-thick" aria-label="Sections">
        <div className="tabbar__inner">
          {barViews.map((v) => {
            const Glyph = GLYPH[v.id] ?? GridIcon;
            return (
              <NavLink key={v.id} to={PATH[v.id]}
                className={({ isActive }) => (isActive ? 'tabbar__tab tabbar__tab--on' : 'tabbar__tab')}>
                <Glyph />
                <span className="tabbar__label">{SHORT[v.id] ?? v.label}</span>
              </NavLink>
            );
          })}
          {moreViews.length > 0 && (
            <button
              className={inMore ? 'tabbar__tab tabbar__tab--on' : 'tabbar__tab'}
              aria-haspopup="dialog"
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen(true)}
            >
              <MoreIcon />
              <span className="tabbar__label">More</span>
            </button>
          )}
        </div>
      </nav>
      )}

      {moreOpen && (
        <Drawer title="Sections" onClose={() => setMoreOpen(false)}>
          <div className="list" style={{ margin: 14, boxShadow: 'none' }}>
            {moreViews.map((v) => {
              const Glyph = GLYPH[v.id] ?? GridIcon;
              return (
                <NavLink key={v.id} to={PATH[v.id]} className="list__row list__row--tiled"
                  onClick={() => setMoreOpen(false)}>
                  <span className="tile tile-blue"><Glyph /></span>
                  <span className="list__label"><span className="list__title">{v.label}</span></span>
                  <span className="list__chev"><ChevronRight /></span>
                </NavLink>
              );
            })}
          </div>
        </Drawer>
      )}
    </div>
  );
}

/** The logbook picker addresses a student's book by query string. */
function LogbookRoute(): JSX.Element {
  const [params] = useSearchParams();
  const student = params.get('student');
  return <Logbook studentId={student ? Number(student) : undefined} />;
}

// Referenced by the account chip; declared here to keep the stylesheet honest.
export { Segmented, GaugeIcon, DocIcon };
